import React from 'react';
import { getAccounts } from '@sp-firebase/accountFB';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import { AdminLoading } from './admin-components/AdminCommon';
import BasicTable from '../FinancialPositions/components/BasicTable';
import { getColDefs, genericFormatter } from '../FinancialPositions/components/BasicTableUtils';
import { Account } from '../../sp-types/accountTypes';
import { LoadingSpinnerOverlay } from '../../components/UI-Elements/LoadingSpinnerOverlay';
import { CheckCircle, Close, DeleteForever, Edit, PlayCircle, Send } from '@mui/icons-material';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { NotificationRule, notificationTypes } from '../../sp-types/notificationRule';
import { OverlayDisplayDiv } from '../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { EditNotificationRule } from './admin-components/EditNotificationRule';
import {
  calcDiff,
  checkDiffCondition,
  colDefs,
  createNotificationFromRule,
  genNewNotifRule,
  getMonthVal,
  notesColDefs,
} from './admin-components/adminNotificationUtils';
import { addNotifRule, deleteNotifRule, getNotifRules, updateNotifRule } from '../../sp-firebase/notificationRulesFB';
import { ConfirmDelete } from '../../components/UI-Elements/FileUpload/ErrorComponents/ConfirmDelete';

import { getExposureById } from '../../sp-firebase/exposureFB';
import { adjustExposureByMaterialPrices, calcExposure } from '../../utils/exposureUtils';
import { useCurrencyRates } from '../../hooks/useCurrencyRates';
import { addNotification, getNotifications } from '../../sp-firebase/notificationsFB';
import { addDays } from 'date-fns';
import { DashboardNotification } from '../../sp-types/notificationType';
import { NotificationBar } from './admin-components/NotificationBar';
import { CloseButton } from '../../components/UI-Elements/Buttons/CloseButton';
import { getDateBefore } from '../../utils/gridUtils';
import { getHistoryPricesSince } from '../../sp-firebase/historyPricesFB';
import { IHistoryPrice } from '../../sp-types';
import { useUser } from '../../hooks/useUser';

export const AdminNotificationRules = () => {
  const [accounts, setAccounts] = React.useState<Account[]>([]);
  const { data: user } = useUser();
  const [gridRules, setGridRules] = React.useState<any[]>();
  const [loading, setLoading] = React.useState<string[] | null>();
  const [showEditRule, setShowEditRule] = React.useState<NotificationRule | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = React.useState<string>('');
  const [preventConfirm, setPreventConfirm] = React.useState<boolean>(false);

  const [filters, setFilters] = React.useState({
    account: '',
    type: '',
    material: '',
  });

  const [notifications, setNotifications] = React.useState<DashboardNotification[] | undefined>();

  const { materials } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();
  const getAllAccounts = async () => {
    setLoading([]);
    const allAccounts = await getAccounts();

    setAccounts(allAccounts || []);

    setLoading(null);
  };

  React.useEffect(() => {
    getAllAccounts();
  }, []);

  const fetchRules = async () => {
    setLoading([]);

    const rules = await getNotifRules(filters.account, filters.material, filters.type.toString(), 'all');

    setGridRules(rules);

    setLoading(null);
  };

  React.useEffect(() => {
    if (filters) {
      fetchRules();
    }
  }, [filters]);

  const columnFormatter = (data: any): React.ReactNode | undefined => {
    if (data?.column.Header === 'isAlreadySent') {
      return (
        <div className="flex flex-1 justify-center">
          {data?.value ? <CheckCircle className="text-green-300" /> : <Close className="text-red-300" />}
        </div>
      );
    }
    if (data?.column.Header === 'actions') {
      return (
        <div className="flex w-32">
          <p>{data?.value}</p>
          <p className="cursor-pointer">
            {user.email === 'moshe@spotlock.co' && (
              <>
                <Button sx={{ minWidth: '' }} onClick={() => setShowEditRule(data.row.original)}>
                  <Edit />
                </Button>

                <Button sx={{ minWidth: '' }} onClick={() => deleteNotification(data.row.original?.id)}>
                  <DeleteForever />
                </Button>
              </>
            )}
            <Button sx={{ minWidth: '' }} onClick={() => checkRule(data.row.original)}>
              <PlayCircle />
            </Button>
          </p>
        </div>
      );
    }

    if (data?.column.Header === 'options') {
      return <span className="inline-block w-60 whitespace-pre-wrap break-words">{JSON.stringify(data?.value)}</span>;
    }
    return genericFormatter(data);
  };

  const deleteNotification = (id: string) => {
    if (preventConfirm) {
      doDelete(id);
    } else {
      setConfirmDelete(id);
    }
  };
  const doDelete = async (id: string) => {
    setLoading([]);
    setConfirmDelete('');

    await deleteNotifRule(id);
    await fetchRules();

    setLoading(null);
  };

  const closeEdit = async (editedRule?: NotificationRule) => {
    if (editedRule && editedRule.type && editedRule.condition) {
      setLoading([]);
      await (editedRule.id ? updateNotifRule(editedRule) : addNotifRule(editedRule));

      await fetchRules();
      setLoading(null);
    }
    setShowEditRule(undefined);
  };

  const isDatePassed = (date: Date, days: number): boolean => addDays(date, days) < new Date();

  const checkRule = async (original: NotificationRule) => {
    if (original) {
      const account = accounts.find((a) => a.id === original.accountId);
      if (account) {
        setLoading([]);
        const notesToSave = await shouldTriggerNotification(original, account);
        setNotifications(notesToSave);
        setLoading(null);
      }
    }
  };

  //TODO consider extract to utils
  const shouldTriggerNotification = async (rule: NotificationRule, account: Account): Promise<any[]> => {
    const result = [];

    if (!rule.enabled) {
      return [];
    }

    //this all code should be moved to factory with instances of notifications, and each should implement its checks

    setLoading(['checking rule ', rule.name, rule.type.toString()]);
    if (rule.type === 'balance_limit') {
      const exposure = await getExposureById(account?.id || '');
      const netExposure = exposure && adjustExposureByMaterialPrices(exposure, pricesMap);

      if (rule.formulaId && netExposure && +rule.condition > 0) {
        const materialExp = calcExposure(netExposure, rule.formulaId);
        if (Math.abs(materialExp) > Math.abs(+rule.condition)) {
          const noteAlreadySent = await isNotificationSent(rule, account.id || '');
          const text = `Material ${rule.formulaId} is out of balance`;

          result.push(createNotificationFromRule(rule, account.id || '', rule.formulaId, text, noteAlreadySent));
        }
      }
    } else if (rule.type === 'price_target') {
      const { compareDays, conditionType } = rule.options || {};
      const currPrice = pricesMap[rule.formulaId];
      const fromDate = getDateBefore(+compareDays || 20);

      const prices = await getHistoryPricesSince(rule.formulaId.replace('c1', '').replace('C1', ''), fromDate);

      const diff = (prices && prices.length > 0 && calcDiff(prices, currPrice)) || 0;

      const isRuleConditionMet = checkDiffCondition(Math.abs(diff), +rule.condition, conditionType === '%', currPrice);

      if (isRuleConditionMet) {
        const noteAlreadySent = await isNotificationSent(rule, account.id || '');
        const text = `Price of ${rule.formulaId} was changed by ${diff}${conditionType} in the last days`;

        result.push(createNotificationFromRule(rule, account.id || '', rule.formulaId, text, noteAlreadySent));
      }
    } else if (['backwardation', 'contango'].includes(rule.type)) {
      const { compareMonth, conditionType } = rule.options || {};

      const lastWeek = getDateBefore(10);

      const prices = await getHistoryPricesSince(rule.formulaId.replace('c1', '').replace('C1', ''), lastWeek);

      const latestPrices = (prices && prices.length > 0 && prices[prices.length - 1]) || ({} as IHistoryPrice);
      const diff = latestPrices.price - getMonthVal(latestPrices, +compareMonth);

      const isRuleConditionMet = checkDiffCondition(
        Math.abs(diff),
        +rule.condition,
        conditionType === '%',
        latestPrices.price,
      );

      if (isRuleConditionMet) {
        const noteAlreadySent = await isNotificationSent(rule, account.id || '');

        const text = `${rule.formulaId} is in  ${diff > 0 ? 'contango' : 'backwardation'} by ${
          rule.condition
        }${conditionType}`;

        result.push(createNotificationFromRule(rule, account.id || '', rule.formulaId, text, noteAlreadySent));
      }
    } else if (rule.type === 'logistic') {
      console.log('logistic notif - to be implemented');
    }

    return result;
  };

  const isNotificationSent = async (rule: NotificationRule, accountId: string): Promise<boolean> => {
    const currNotifications = (await getNotifications(accountId)) || [];

    const existNotifications = currNotifications.filter(
      (a) =>
        a.formulaId?.includes(rule.formulaId) &&
        a.ruleType === rule.type &&
        !isDatePassed(a.date?.toDate(), rule.sendEveryDays),
    );

    return existNotifications?.length > 0;
  };

  const saveNotifications = async () => {
    if (notifications) {
      setLoading([]);

      await Promise.all(
        notifications
          .filter((n) => !n.isAlreadySent)
          .map((notification: DashboardNotification) => addNotification(notification)),
      );

      setNotifications(undefined);
      setLoading(null);
    }
  };

  const checkAllRules = async () => {
    if (!gridRules) {
      return;
    }
    setLoading([]);
    const allResults = await Promise.all(
      gridRules.map(async (rule) => {
        const account = accounts.find((a) => a.id === rule.accountId);
        if (!account) {
          return [];
        }
        const notesToSave = await shouldTriggerNotification(rule, account);
        return notesToSave;
      }),
    );
    const allNotes = allResults.reduce((acc, itm) => [...acc, ...itm], []);

    setNotifications(allNotes);

    setLoading(null);
  };
  return accounts ? (
    <div className="flex flex-col flex-1 bg-white p-3">
      {showEditRule && <EditNotificationRule rule={showEditRule} close={closeEdit} accounts={accounts} />}
      {confirmDelete && (
        <ConfirmDelete
          onAction={() => doDelete(confirmDelete)}
          onCancel={() => setConfirmDelete('')}
          title="Confirm delete"
          description={
            <div className="flex flex-col gap-4 items-center">
              <p className="text-lg text-bolder">Are you sure you want to delete this?</p>

              <FormControlLabel
                control={
                  <Checkbox checked={preventConfirm} onChange={({ target }) => setPreventConfirm(target.checked)} />
                }
                label={<p className="text-xs">Prevent this dialog in next deletes?</p>}
              />
            </div>
          }
        />
      )}
      <NotificationBar
        {...{ accounts, filters, materials, notificationTypes, setFilters, checkAllRules }}
        onAddRule={() => setShowEditRule(genNewNotifRule())}
      />
      {gridRules && (
        <div className="flex flex-col gap-2">
          <div className="flex h-[90%] overflow-auto">
            <BasicTable
              allRows={gridRules}
              columns={getColDefs(colDefs, columnFormatter, false).filter((c) => c.name !== 'ID')}
              options={{
                cellClass: 'max-h-24',
                noBorders: true,
                filters: { status: true, text: true },
                noDataMsg: 'No data',
              }}
            ></BasicTable>
          </div>
        </div>
      )}

      {notifications && notifications.length > 0 && (
        <OverlayDisplayDiv setDisplayOverlay={() => setNotifications(undefined)} hideClose>
          <div className="w-fit h-200 relative bg-white rounded shadow p-3 flex flex-col gap-5">
            <div className="absolute top-3 right-3">
              <CloseButton onClick={() => setNotifications(undefined)} />
            </div>
            <h4>Notifications to create</h4>
            <BasicTable
              allRows={notifications}
              columns={getColDefs(notesColDefs, columnFormatter, false).filter((c) => c.name !== 'ID')}
              options={{
                cellClass: 'max-h-24',
                noBorders: true,
                noDataMsg: 'No data',
              }}
            ></BasicTable>

            <div className="flex justify-center">
              <Button onClick={saveNotifications} variant="contained" disabled={!(notifications?.length > 0)}>
                <div className="flex gap-2">
                  <Send /> Send notifications
                </div>
              </Button>
            </div>
          </div>
        </OverlayDisplayDiv>
      )}

      {loading && <LoadingSpinnerOverlay size={64}>{loading.join(' ')}</LoadingSpinnerOverlay>}
    </div>
  ) : (
    <AdminLoading />
  );
};
